import {
  createDatabaseShardManifest,
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  type DatabaseShardManifest,
  type DatabaseShardManifestEntry,
  normalizeDatabaseShardManifest,
} from '@blocksuite/affine-model';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { encodeStateVector } from 'yjs';

import {
  type DatabaseRowShardHydrationProgress,
  type DatabaseRowShardIntegrityIssue,
  type DatabaseRowShardReadiness,
  type DatabaseRowShardRepairResult,
  type DatabaseRowShardSnapshotBarrierContext,
} from './data-source-contracts.js';
import type { DatabaseSourceRuntime } from './data-source-runtime-context.js';
import {
  INITIAL_DATABASE_ROW_WINDOW_LIMIT,
  rowShardSnapshotBarriers,
} from './data-source-values.js';

export function materializeAllShardedRows(this: DatabaseSourceRuntime): void {
  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  if (!manifest) return;

  const expectedRows = this.getManifestRowCount(manifest);
  if (
    this._shardedRefreshFrame === null &&
    this._shardedRowIds.length === expectedRows &&
    this._shardedRowsById.size === expectedRows &&
    this.areAllManifestShardRowsMaterialized(manifest)
  ) {
    return;
  }

  for (const shard of manifest.shards) {
    this.ensureShardObserved(shard, 'relation');
  }
  this.refreshShardedRowsFromDocs(manifest.shards);
}

export function getManifestRowCount(
  this: DatabaseSourceRuntime,
  manifest: ReturnType<typeof normalizeDatabaseShardManifest>
): number {
  if (!manifest) return 0;
  return manifest.shards.reduce((sum, shard) => sum + shard.rowCount, 0);
}

export function getManifestShardsForRowsWindow(
  this: DatabaseSourceRuntime,
  manifest: ReturnType<typeof normalizeDatabaseShardManifest>,
  offset: number,
  limit: number
): DatabaseShardManifestEntry[] {
  if (!manifest) return [];
  const endOffset = offset + limit;
  let shardStart = 0;
  const shards: DatabaseShardManifestEntry[] = [];
  for (const shard of manifest.shards) {
    const shardEnd = shardStart + shard.rowCount;
    if (shardEnd > offset && shardStart < endOffset) {
      shards.push(shard);
    }
    if (shardStart >= endOffset) break;
    shardStart = shardEnd;
  }
  return shards;
}

export function getInitialRowWindowReadiness(
  this: DatabaseSourceRuntime,
  limit: number = INITIAL_DATABASE_ROW_WINDOW_LIMIT
): DatabaseRowShardReadiness {
  if (!this.isRowSharded) {
    return { status: 'ready' };
  }

  this._shardedRowsRevision.value;
  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  if (!manifest) {
    return { status: 'ready' };
  }

  const integrityIssue = this.getAuthoritativeRowShardIntegrityIssue(manifest);
  if (integrityIssue) {
    return {
      message: integrityIssue.message,
      repairable: integrityIssue.repairable,
      status: 'error',
    };
  }
  const pendingIntegrityIssue = this.getLoadedRowShardIntegrityIssue(manifest);
  if (pendingIntegrityIssue) {
    this.scheduleLoadedRowShardIntegrityValidation(
      manifest,
      pendingIntegrityIssue
    );
    return { status: 'loading' };
  }

  // Readiness owns the load it waits for. A cold workspace can already have
  // the shard doc registered locally while its remote snapshot has not been
  // requested yet. Inspecting that empty shell before requesting the initial
  // row window creates a deadlock: callers keep polling `loading`, but no
  // observer ever triggers the row-shard load.
  const initialShards =
    this.isFileIndexDatabase() && this.getManifestRowCount(manifest) === 0
      ? manifest.shards.slice(0, 1)
      : this.getManifestShardsForRowsWindow(manifest, 0, limit);
  for (const shard of initialShards) {
    this.ensureShardObserved(shard, 'row-window');
  }

  const emptyIntegrityIssue = this.getLoadedRowShardIntegrityIssue(
    manifest,
    true
  );
  if (emptyIntegrityIssue) {
    const snapshot = this.captureLoadedRowShardSnapshot(manifest, true);
    if (snapshot && this._rowShardEmptyConfirmedSnapshot === snapshot) {
      this.scheduleLoadedRowShardManifestAutoRepair(
        manifest,
        emptyIntegrityIssue
      );
      return {
        message: emptyIntegrityIssue.message,
        repairable: emptyIntegrityIssue.repairable,
        status: 'error',
      };
    }
    this.scheduleLoadedEmptyRowShardConfirmation(
      manifest,
      emptyIntegrityIssue,
      snapshot
    );
    return { status: 'loading' };
  }
  if (
    this.getManifestRowCount(manifest) === 0 &&
    (!this.isFileIndexDatabase() || manifest.shards.length === 0)
  ) {
    return { status: 'ready' };
  }

  if (initialShards.length === 0) {
    return { status: 'ready' };
  }

  let ready = true;
  for (const shard of initialShards) {
    const doc = this.ensureShardObserved(shard, 'row-window');
    if (!doc.ready || !this.hasCompleteLoadedShardRows(shard, doc)) {
      ready = false;
    }
  }

  if (ready) {
    this.refreshShardedRowsFromDocs(initialShards);
  }

  return ready ? { status: 'ready' } : { status: 'loading' };
}

export function getContentReadiness(
  this: DatabaseSourceRuntime
): DatabaseRowShardReadiness {
  if (!this.isRowSharded) {
    return { status: 'ready' };
  }

  this._shardedRowsRevision.value;
  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  if (!manifest) {
    return { status: 'ready' };
  }

  for (const shard of manifest.shards) {
    this.ensureShardObserved(shard, 'row-window');
  }
  this.refreshShardedRowsFromDocs(manifest.shards);

  const integrityIssue = this.getAuthoritativeRowShardIntegrityIssue(manifest);
  if (integrityIssue) {
    return {
      message: integrityIssue.message,
      repairable: integrityIssue.repairable,
      status: 'error',
    };
  }

  const pendingIntegrityIssue = this.getLoadedRowShardIntegrityIssue(manifest);
  const progress = this.getRowShardHydrationProgress(manifest);
  if (pendingIntegrityIssue) {
    this.scheduleLoadedRowShardIntegrityValidation(
      manifest,
      pendingIntegrityIssue
    );
    return { progress, status: 'loading' };
  }

  return progress.readyShards === progress.totalShards
    ? { progress, status: 'ready' }
    : { progress, status: 'loading' };
}

export function isInitialRowWindowReady(
  this: DatabaseSourceRuntime,
  limit: number = INITIAL_DATABASE_ROW_WINDOW_LIMIT
): boolean {
  return this.getInitialRowWindowReadiness(limit).status === 'ready';
}

export function getLoadedRowShardIntegrityIssue(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest,
  includeEmptyManifestMismatch = false
): DatabaseRowShardIntegrityIssue | null {
  if (this.isFileIndexDatabase()) return null;

  for (const shard of manifest.shards) {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) continue;

    const rowOrder = shardDoc.spaceDoc
      .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
      .toArray()
      .map(value => String(value));
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    const uniqueRowIds = new Set(rowOrder);
    const missingRowIds = rowOrder.filter(rowId => !rowsMap.has(rowId));
    const unindexedRowIds = [...rowsMap.keys()].filter(
      rowId => !uniqueRowIds.has(String(rowId))
    );
    const boundaryMetadataMatches =
      shard.firstRowId === rowOrder[0] && shard.lastRowId === rowOrder.at(-1);

    // A newly registered shard can be ready before its local/remote snapshot
    // lands. Empty state remains loading; non-empty contradictory state is a
    // persisted integrity failure and must not spin forever as "loading".
    if (
      !includeEmptyManifestMismatch &&
      rowOrder.length === 0 &&
      rowsMap.size === 0 &&
      shard.rowCount > 0
    ) {
      continue;
    }

    if (
      uniqueRowIds.size === rowOrder.length &&
      missingRowIds.length === 0 &&
      unindexedRowIds.length === 0 &&
      rowOrder.length === shard.rowCount &&
      boundaryMetadataMatches
    ) {
      continue;
    }

    return {
      message: [
        `Database \"${this.databaseId}\" cannot load because row shard `,
        `\"${shard.shardId}\" is inconsistent: manifest expects `,
        `${shard.rowCount} rows, row order contains ${rowOrder.length}, `,
        `row storage contains ${rowsMap.size}, and boundary metadata `,
        `${boundaryMetadataMatches ? 'matches' : 'does not match'} the stored order.`,
      ].join(''),
      repairable: missingRowIds.length === 0 && unindexedRowIds.length === 0,
      rowCount: rowOrder.length,
      shardId: shard.shardId,
    };
  }

  return null;
}

export function getAuthoritativeRowShardIntegrityIssue(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest
): DatabaseRowShardIntegrityIssue | null {
  const issue = this.getLoadedRowShardIntegrityIssue(manifest);
  if (!issue) {
    this._rowShardAuthoritativeIntegritySnapshot = undefined;
    return null;
  }
  if (!rowShardSnapshotBarriers.get(this._hostModel.store)?.size) {
    return issue;
  }
  const snapshot = this.captureLoadedRowShardSnapshot(manifest);
  return snapshot && snapshot === this._rowShardAuthoritativeIntegritySnapshot
    ? issue
    : null;
}

export function getRowShardHydrationProgress(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest
): DatabaseRowShardHydrationProgress {
  let loadedRows = 0;
  let readyShards = 0;
  for (const shard of manifest.shards) {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) continue;
    const rowOrder = shardDoc.spaceDoc.getArray<string>(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    loadedRows += Math.min(shard.rowCount, rowOrder.length, rowsMap.size);
    if (this.hasCompleteLoadedShardRows(shard, shardDoc)) {
      readyShards += 1;
    }
  }
  return {
    loadedRows,
    readyShards,
    totalRows: this.getManifestRowCount(manifest),
    totalShards: manifest.shards.length,
  };
}

export function captureLoadedRowShardSnapshot(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest,
  _includeEmptyManifestMismatch = false
): string | null {
  const shards: Array<{
    docId: string;
    rowOrder: string[];
    rowStorageIds: string[];
    stateVector: string;
  }> = [];

  for (const shard of manifest.shards) {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) return null;
    const rowOrder = shardDoc.spaceDoc
      .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
      .toArray()
      .map(value => String(value));
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    shards.push({
      docId: shardDoc.id,
      rowOrder,
      rowStorageIds: [...rowsMap.keys()].map(String).sort(),
      stateVector: encodeStateVector(shardDoc.spaceDoc).toString(),
    });
  }

  return JSON.stringify({
    manifest,
    rootStateVector: encodeStateVector(this.storageDoc.spaceDoc).toString(),
    shards,
  });
}

export function scheduleLoadedEmptyRowShardConfirmation(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest,
  issue: DatabaseRowShardIntegrityIssue,
  beforeSnapshot: string | null
): void {
  const barriers = rowShardSnapshotBarriers.get(this._hostModel.store);
  if (
    this._disposed ||
    this.readonly$.value ||
    !issue.repairable ||
    !beforeSnapshot ||
    !barriers?.size ||
    this._rowShardAutoRepairInFlight ||
    this._rowShardEmptyConfirmationAttemptedSnapshot === beforeSnapshot
  ) {
    return;
  }

  this._rowShardEmptyConfirmationAttemptedSnapshot = beforeSnapshot;
  const abort = new AbortController();
  this._rowShardAutoRepairAbort?.abort();
  this._rowShardAutoRepairAbort = abort;
  const context: DatabaseRowShardSnapshotBarrierContext = {
    blockId: this.blockId,
    databaseId: this.databaseId,
    rootDocId: this.storageDoc.id,
    rowShardDocIds: manifest.shards.map(shard => shard.serverDocId),
    signal: abort.signal,
    workspaceId: this._model.store.workspace.id,
  };
  const barriersForAttempt = [...barriers];

  this._rowShardAutoRepairInFlight = Promise.all(
    barriersForAttempt.map(barrier => barrier(context))
  )
    .then(() => {
      if (this._disposed || abort.signal.aborted || this.readonly$.value) {
        return;
      }
      const currentManifest = normalizeDatabaseShardManifest(
        this._model.props.databaseShardManifest
      );
      const currentIssue = currentManifest
        ? this.getLoadedRowShardIntegrityIssue(currentManifest, true)
        : null;
      const afterSnapshot = currentManifest
        ? this.captureLoadedRowShardSnapshot(currentManifest, true)
        : null;
      if (
        currentIssue?.repairable &&
        currentIssue.message === issue.message &&
        afterSnapshot === beforeSnapshot
      ) {
        this._rowShardEmptyConfirmedSnapshot = beforeSnapshot;
      }
    })
    .catch(() => {
      // Keep the shard in loading state. A failed confirmation barrier never
      // authorizes a manifest rewrite from an unconfirmed empty snapshot.
    })
    .finally(() => {
      if (this._rowShardAutoRepairAbort === abort) {
        this._rowShardAutoRepairAbort = undefined;
      }
      this._rowShardAutoRepairInFlight = undefined;
      this._shardedRowsRevision.value += 1;
    });
}

export function scheduleLoadedRowShardIntegrityValidation(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest,
  issue: DatabaseRowShardIntegrityIssue
): void {
  const barriers = rowShardSnapshotBarriers.get(this._hostModel.store);
  const beforeSnapshot = this.captureLoadedRowShardSnapshot(manifest);
  if (
    this._disposed ||
    !beforeSnapshot ||
    !barriers?.size ||
    this._rowShardIntegrityValidationInFlight ||
    this._rowShardIntegrityValidationAttemptedSnapshot === beforeSnapshot
  ) {
    return;
  }

  this._rowShardIntegrityValidationAttemptedSnapshot = beforeSnapshot;
  const abort = new AbortController();
  this._rowShardIntegrityValidationAbort?.abort();
  this._rowShardIntegrityValidationAbort = abort;
  const context: DatabaseRowShardSnapshotBarrierContext = {
    blockId: this.blockId,
    databaseId: this.databaseId,
    rootDocId: this.storageDoc.id,
    rowShardDocIds: manifest.shards.map(shard => shard.serverDocId),
    signal: abort.signal,
    workspaceId: this._model.store.workspace.id,
  };
  const barriersForAttempt = [...barriers];

  this._rowShardIntegrityValidationInFlight = Promise.all(
    barriersForAttempt.map(barrier => barrier(context))
  )
    .then(() => {
      if (this._disposed || abort.signal.aborted) return;
      const currentManifest = normalizeDatabaseShardManifest(
        this._model.props.databaseShardManifest
      );
      const currentIssue = currentManifest
        ? this.getLoadedRowShardIntegrityIssue(currentManifest)
        : null;
      const afterSnapshot = currentManifest
        ? this.captureLoadedRowShardSnapshot(currentManifest)
        : null;
      if (!currentIssue || currentIssue.message !== issue.message) return;
      if (afterSnapshot === beforeSnapshot) {
        this._rowShardAuthoritativeIntegritySnapshot = afterSnapshot;
        this._rowShardIntegrityError.value = currentIssue.message;
        this.queueShardedRowsRefresh();
      }
    })
    .catch(() => {
      // A later consumer read may retry after a transient barrier failure.
      if (
        !this._disposed &&
        !abort.signal.aborted &&
        this._rowShardIntegrityValidationAttemptedSnapshot === beforeSnapshot
      ) {
        this._rowShardIntegrityValidationAttemptedSnapshot = undefined;
      }
    })
    .finally(() => {
      if (this._rowShardIntegrityValidationAbort === abort) {
        this._rowShardIntegrityValidationAbort = undefined;
      }
      this._rowShardIntegrityValidationInFlight = undefined;
    });
}

export function scheduleLoadedRowShardManifestAutoRepair(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest,
  issue: DatabaseRowShardIntegrityIssue
): void {
  // The preloader is owned by the visible host page. Sharded databases keep
  // durable storage in a separate root Store, so looking up by _model.store
  // disconnects the production path while same-store tests still pass.
  const barriers = rowShardSnapshotBarriers.get(this._hostModel.store);
  const beforeSnapshot = this.captureLoadedRowShardSnapshot(manifest, true);
  if (
    this._disposed ||
    this.readonly$.value ||
    !issue.repairable ||
    !beforeSnapshot ||
    !barriers?.size ||
    this._rowShardAutoRepairInFlight ||
    this._rowShardAutoRepairAttemptedSnapshot === beforeSnapshot
  ) {
    return;
  }

  this._rowShardAutoRepairAttemptedSnapshot = beforeSnapshot;
  const abort = new AbortController();
  this._rowShardAutoRepairAbort?.abort();
  this._rowShardAutoRepairAbort = abort;
  const context: DatabaseRowShardSnapshotBarrierContext = {
    blockId: this.blockId,
    databaseId: this.databaseId,
    rootDocId: this.storageDoc.id,
    rowShardDocIds: manifest.shards.map(shard => shard.serverDocId),
    signal: abort.signal,
    workspaceId: this._model.store.workspace.id,
  };
  const barriersForAttempt = [...barriers];
  const crossDocSyncBarrier = () =>
    Promise.all(barriersForAttempt.map(barrier => barrier(context)));

  this._rowShardAutoRepairInFlight = crossDocSyncBarrier()
    .then(async () => {
      if (this._disposed || abort.signal.aborted || this.readonly$.value) {
        return;
      }
      const currentManifest = normalizeDatabaseShardManifest(
        this._model.props.databaseShardManifest
      );
      const currentIssue = currentManifest
        ? this.getLoadedRowShardIntegrityIssue(currentManifest, true)
        : null;
      const afterSnapshot = currentManifest
        ? this.captureLoadedRowShardSnapshot(currentManifest, true)
        : null;
      if (
        !currentIssue?.repairable ||
        currentIssue.message !== issue.message ||
        afterSnapshot !== beforeSnapshot
      ) {
        return;
      }
      this.repairLoadedRowShardManifestDrift(beforeSnapshot);
      // The same canonical barrier now flushes the loaded-shard delta and
      // the root-manifest update, then waits for backend ACK/global sync
      // quiescence. This closes the outbox boundary without inventing a
      // second persistence writer or merging a fresh full-state Y.Doc.
      await crossDocSyncBarrier();
      const repairedManifest = normalizeDatabaseShardManifest(
        this._model.props.databaseShardManifest
      );
      if (
        !repairedManifest ||
        this.getLoadedRowShardIntegrityIssue(repairedManifest, true)
      ) {
        throw new BlockSuiteError(
          ErrorCode.DatabaseBlockError,
          'Database row-shard auto-repair did not reach a consistent synced readback.'
        );
      }
    })
    .catch(() => {
      // The database-local error and manual recovery action remain visible.
      // A failed/aborted barrier never broadens into an unfenced write.
    })
    .finally(() => {
      if (this._rowShardAutoRepairAbort === abort) {
        this._rowShardAutoRepairAbort = undefined;
      }
      this._rowShardAutoRepairInFlight = undefined;
    });
}

export function repairLoadedRowShardManifestDrift(
  this: DatabaseSourceRuntime,
  expectedSnapshot?: string
): DatabaseRowShardRepairResult {
  if (this.readonly$.value) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Database row-shard repair requires edit permission.'
    );
  }
  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  if (!manifest) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database \"${this.databaseId}\" has no valid row-shard manifest.`
    );
  }
  if (
    expectedSnapshot !== undefined &&
    this.captureLoadedRowShardSnapshot(manifest, true) !== expectedSnapshot
  ) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Database row-shard snapshot changed before repair; refusing stale metadata repair.'
    );
  }

  const repairPlans = manifest.shards.map(shard => {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database row shard "${shard.shardId}" is not ready for repair.`
      );
    }
    const rowOrder = shardDoc.spaceDoc.getArray<string>(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    const storedRowOrder = rowOrder.toArray().map(value => String(value));
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    const canonicalRowOrder: string[] = [];
    const uniqueRowIds = new Set<string>();
    const duplicateIndexes: number[] = [];
    storedRowOrder.forEach((rowId, index) => {
      if (uniqueRowIds.has(rowId)) {
        duplicateIndexes.push(index);
        return;
      }
      uniqueRowIds.add(rowId);
      canonicalRowOrder.push(rowId);
    });
    const rowOrderMatchesStorage =
      storedRowOrder.every(rowId => rowsMap.has(rowId)) &&
      [...rowsMap.keys()].every(rowId => uniqueRowIds.has(String(rowId)));
    if (!rowOrderMatchesStorage) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database row shard \"${shard.shardId}\" cannot be repaired automatically because row order and row storage differ.`
      );
    }
    const manifestMatchesCanonicalOrder =
      shard.rowCount === canonicalRowOrder.length &&
      shard.firstRowId === canonicalRowOrder[0] &&
      shard.lastRowId === canonicalRowOrder.at(-1);
    return {
      canonicalRowOrder,
      duplicateIndexes,
      manifestMatchesCanonicalOrder,
      rowOrder,
      rowsMap,
      shard,
      shardDoc,
    };
  });

  const repairedShardCount = repairPlans.filter(
    plan =>
      plan.duplicateIndexes.length > 0 || !plan.manifestMatchesCanonicalOrder
  ).length;
  const rowCount = repairPlans.reduce(
    (count, plan) => count + plan.canonicalRowOrder.length,
    0
  );

  if (repairedShardCount === 0) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'No safely repairable row-shard manifest drift is currently loaded.'
    );
  }

  // Every shard was validated against the same exact state-vector snapshot
  // above. Mutate the already loaded Y.Doc so sync emits only its Yjs delta;
  // never rebuild/merge a fresh full-state document for repair.
  for (const plan of repairPlans) {
    if (plan.duplicateIndexes.length === 0) continue;
    plan.shardDoc.spaceDoc.transact(() => {
      for (const index of [...plan.duplicateIndexes].reverse()) {
        plan.rowOrder.delete(index, 1);
      }
    });
  }

  const repairedManifest = createDatabaseShardManifest({
    ...manifest,
    shards: repairPlans.map(plan => ({
      ...plan.shard,
      firstRowId: plan.canonicalRowOrder[0],
      lastRowId: plan.canonicalRowOrder.at(-1),
      rowCount: plan.canonicalRowOrder.length,
    })),
  });
  this.captureStorageSync();
  this.storageDoc.transact(() => {
    this._model.props.databaseShardManifest = repairedManifest;
  });

  for (const [index, plan] of repairPlans.entries()) {
    const readbackOrder = plan.rowOrder.toArray().map(rowId => String(rowId));
    const readbackUnique = new Set(readbackOrder);
    const readbackManifest = repairedManifest.shards[index];
    if (
      readbackOrder.length !== readbackUnique.size ||
      readbackOrder.length !== plan.rowsMap.size ||
      readbackOrder.length !== readbackManifest?.rowCount ||
      readbackManifest.firstRowId !== readbackOrder[0] ||
      readbackManifest.lastRowId !== readbackOrder.at(-1)
    ) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database row shard "${plan.shard.shardId}" repair readback failed.`
      );
    }
  }
  this._rowShardIntegrityError.value = null;
  this._rowShardAutoRepairAttemptedSnapshot = undefined;
  this._rowShardIntegrityValidationAttemptedSnapshot = undefined;
  this._rowShardAuthoritativeIntegritySnapshot = undefined;
  this._rowShardEmptyConfirmationAttemptedSnapshot = undefined;
  this._rowShardEmptyConfirmedSnapshot = undefined;
  this.refreshShardedRowsFromDocs(repairedManifest.shards);

  return { repairedShardCount, rowCount };
}

export function areAllManifestShardRowsMaterialized(
  this: DatabaseSourceRuntime,
  manifest: ReturnType<typeof normalizeDatabaseShardManifest>
): boolean {
  if (!manifest) return false;
  return manifest.shards.every(shard => {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) return false;
    return this.hasCompleteLoadedShardRows(shard, shardDoc);
  });
}
